import React, { useState, useEffect } from "react";
import {
  BarChart2,
  BookOpen,
  Calendar,
  Settings as SettingsIcon,
  LogOut,
  Upload,
  Binary,
  GraduationCap,
  Plus,
  Target,
} from "lucide-react";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import ProfileAvatar from "../Profile/ProfileAvatar";
import { useTheme } from "../../context/ThemeContext";
import MetricsDashboard from "./MetricsDashboard";
import CustomizableDashboard from "./CustomizableDashboard";
import { useDashboardCustomization } from "../../context/DashboardCustomizationContext";
import ImportTrades from "../ImportTrades/ImportTrades";
import TradeLog from "../TradeLog/TradeLog";
import Settings from "../Settings/Settings";
import Journal from "../Journal/Journal";
import UserProfile from "../Profile/UserProfile";
import StratBuilder from "../StratBuilder/StratBuilder";
import DevelopmentHub from "../DevelopmentHub/DevelopmentHub";
import ValueInsights from "../ValueInsights/ValueInsights";
import calculateTradeMetrics from "../../utils/tradeMetrics";
import { getUserTrades } from "../../utils/firebaseTradeUtils";
import { useAuth } from "../../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import GameButton from "../Games/FlappyTrade/GameButton";
import FlappyTrade from "../Games/FlappyTrade/FlappyTrade";
import StockWordle from "../Games/StockWordle/StockWordle";
import WordGameButton from "../Games/StockWordle/WordGameButton";
import Clock from "./Clock";

const Dashboard = ({ onLogout }) => {
  const [activeMenuItem, setActiveMenuItem] = useState("dashboard");
  const [tradeData, setTradeData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [showGame, setShowGame] = useState(false);
  const [showWordGame, setShowWordGame] = useState(false);
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [userSettings, setUserSettings] = useState(null);
  const { isCustomizing, shelfMetrics, addMetricToDashboard } =
    useDashboardCustomization();
  const [masteryScoreData, setMasteryScoreData] = useState(null);
  const [draggedMetric, setDraggedMetric] = useState(null);

  // Updated menuItems array with ValueInsights
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart2 },
    { id: "tradelog", label: "Trade Log", icon: Calendar },
    { id: "journal", label: "Journal", icon: BookOpen },
    { id: "stratbuilder", label: "Strat Builder", icon: Binary },
    { id: "development", label: "Development Hub", icon: GraduationCap },
    { id: "valueinsights", label: "Value Insights", icon: Target },
    { id: "settings", label: "Settings", icon: SettingsIcon },
  ];

  const renderTitle = (menuItem) => {
    switch (menuItem) {
      case "tradelog":
        return "Trade Log";
      case "dashboard":
        return isCustomizing ? "Customize Dashboard" : "Dashboard";
      case "journal":
        return "Journal";
      case "settings":
        return "Settings";
      case "profile":
        return "Profile";
      case "import":
        return "Import Trades";
      case "stratbuilder":
        return "Strat Builder";
      case "development":
        return "Development Hub";
      case "valueinsights":
        return "Value Insights";
      default:
        return menuItem.charAt(0).toUpperCase() + menuItem.slice(1);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        console.log("Fetching trades for user:", user.uid);
        const fetchedTrades = await getUserTrades(user.uid);
        console.log("Raw fetched trades:", fetchedTrades);

        // Add this to check the trade structure
        if (fetchedTrades.length > 0) {
          console.log("Sample trade structure:", fetchedTrades[0]);
          console.log("Trade pnl type:", typeof fetchedTrades[0].pnl);
          console.log(
            "Trade entry price type:",
            typeof fetchedTrades[0].entryPrice
          );
          console.log(
            "Trade exit price type:",
            typeof fetchedTrades[0].exitPrice
          );
          console.log("Trade date:", fetchedTrades[0].date);
        }

        setTrades(fetchedTrades);
        const metrics = calculateTradeMetrics(fetchedTrades);
        console.log("Calculated metrics:", metrics);
        setTradeData(metrics);
      } catch (error) {
        console.error("Error fetching trades:", error);
      }
    };

    fetchData();

    const handleTradeUpdate = () => {
      console.log("Trade update event received");
      fetchData();
    };

    window.addEventListener("tradesUpdated", handleTradeUpdate);
    window.addEventListener("storage", handleTradeUpdate);

    // Force a refresh when the component mounts
    setTimeout(() => {
      console.log("Forcing initial data refresh");
      handleTradeUpdate();
    }, 1000);

    return () => {
      window.removeEventListener("tradesUpdated", handleTradeUpdate);
      window.removeEventListener("storage", handleTradeUpdate);
    };
  }, [user]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;
      try {
        const settingsRef = doc(db, "userSettings", user.uid);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          console.log("Fetched settings:", data);
          setUserSettings(data);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();

    const handleSettingsUpdate = () => {
      console.log("Settings updated event received");
      fetchSettings();
    };

    window.addEventListener("settingsUpdated", handleSettingsUpdate);
    return () =>
      window.removeEventListener("settingsUpdated", handleSettingsUpdate);
  }, [user]);

  // Add a useEffect to listen for skills data updates from Development Hub
  useEffect(() => {
    const handleMasteryScoreUpdate = (event) => {
      setMasteryScoreData(event.detail);
    };

    window.addEventListener("masteryScoreUpdated", handleMasteryScoreUpdate);

    return () => {
      window.removeEventListener(
        "masteryScoreUpdated",
        handleMasteryScoreUpdate
      );
    };
  }, []);

  const handleImportClick = () => {
    setActiveMenuItem("import");
  };

  // Drag and drop handlers for shelf metrics
  const handleDragStart = (e, metricId) => {
    setDraggedMetric(metricId);
    e.dataTransfer.setData("text/plain", metricId);
    // Make the drag image transparent
    const img = new Image();
    img.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const metricId = e.dataTransfer.getData("text/plain");
    if (metricId) {
      addMetricToDashboard(metricId);
      setDraggedMetric(null);
    }
  };

  // Render the metrics shelf or regular menu based on customization mode
  const renderSidebar = () => {
    if (isCustomizing) {
      return (
        <>
          <div className="menu-header">
            <h2>Available Metrics</h2>
          </div>

          <div className="customization-help">
            <p>Drag metrics to rearrange</p>
            <p>Use the minimize button to move metrics to shelf</p>
            <p>Drag metrics from shelf to dashboard</p>
          </div>

          <div className="shelf-items">
            {shelfMetrics.map((metric) => (
              <div
                key={metric.id}
                className="shelf-item"
                draggable
                onDragStart={(e) => handleDragStart(e, metric.id)}
                onClick={() => addMetricToDashboard(metric.id)}
              >
                <div className="shelf-item-name">{metric.name}</div>
                <div className="shelf-item-icon">
                  <Plus size={16} />
                </div>
              </div>
            ))}
          </div>

          <div className="menu-footer">
            {userSettings?.enableClock && (
              <Clock timezone={userSettings.selectedTimezone} />
            )}
            <ProfileAvatar onClick={() => setActiveMenuItem("profile")} />
            <span className="version-text">Version 2.05 Beta</span>
            <ThemeToggle />
            <button className="logout-button" onClick={onLogout}>
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="menu-header">
            <h2>Mastery Method</h2>
          </div>

          <nav className="menu-items">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`menu-item ${
                    activeMenuItem === item.id ? "active" : ""
                  }`}
                  onClick={() => setActiveMenuItem(item.id)}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="menu-footer">
            {userSettings?.enableClock && (
              <Clock timezone={userSettings.selectedTimezone} />
            )}
            <ProfileAvatar onClick={() => setActiveMenuItem("profile")} />
            <span className="version-text">Version 2.05 Beta</span>
            <ThemeToggle />
            <button className="logout-button" onClick={onLogout}>
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </>
      );
    }
  };

  // Updated renderContent to include ValueInsights component
  const renderContent = () => {
    // When in customization mode, only show dashboard regardless of active menu item
    if (isCustomizing) {
      return (
        <div className="customizable-content-wrapper">
          <CustomizableDashboard
            trades={trades}
            userSettings={userSettings}
            tradeData={tradeData}
            masteryScoreData={masteryScoreData}
          />
        </div>
      );
    }

    switch (activeMenuItem) {
      case "dashboard":
        return (
          <CustomizableDashboard
            trades={trades}
            userSettings={userSettings}
            tradeData={tradeData}
            masteryScoreData={masteryScoreData}
          />
        );
      case "import":
        return <ImportTrades onBack={() => setActiveMenuItem("dashboard")} />;
      case "tradelog":
        return <TradeLog />;
      case "journal":
        return <Journal />;
      case "stratbuilder":
        return <StratBuilder />;
      case "development":
        return <DevelopmentHub />;
      case "valueinsights":
        return <ValueInsights />;
      case "settings":
        return <Settings />;
      case "profile":
        return <UserProfile />;
      default:
        return null;
    }
  };

  return (
    <div className={`dashboard-container ${isDarkMode ? "dark" : "light"}`}>
      <div className="dashboard-menu">{renderSidebar()}</div>

      <div
        className={`dashboard-content ${
          isCustomizing ? "customizing-content" : ""
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="content-header">
          <h1>{renderTitle(activeMenuItem)}</h1>
          <div className="content-header-buttons">
            {activeMenuItem !== "import" &&
              activeMenuItem !== "profile" &&
              !isCustomizing && (
                <button className="import-button" onClick={handleImportClick}>
                  <Upload size={18} />
                  <span>Import Trades</span>
                </button>
              )}
          </div>
        </div>
        {renderContent()}
      </div>

      <WordGameButton onClick={() => setShowWordGame(true)} />
      <GameButton onClick={() => setShowGame(true)} />
      {showGame && <FlappyTrade onClose={() => setShowGame(false)} />}
      {showWordGame && <StockWordle onClose={() => setShowWordGame(false)} />}
    </div>
  );
};

export default Dashboard;
